import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { Helmet } from "react-helmet";
import { toast } from "react-toastify";

import { getClientes, updateCliente } from "../api/admin.api";
import useAuth from "../hooks/useAuth";
import EditClient from "../components/edit-client";

import "./admin-clients.css";
import "./pagination.css";

const AdminClients = (props) => {
  // Currently authenticated user
  const { logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // List of client records returned by the API
  const [clientes, setClientes] = useState([]);

  // True while the API request is in-flight
  const [loading, setLoading] = useState(true);
  const [failedLogoIds, setFailedLogoIds] = useState(() => new Set());

  // Stores an error message string when the fetch fails
  const [error, setError] = useState(null);

  // Pagination state: current page, page size, and total record count from the API
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total_count: 0,
  });

  // Fetches one page of clients from the API and updates state
  const fetchClientes = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getClientes({
        page: pagination.page,
        limit: pagination.limit,
      });
      setClientes(data.items ?? []);
      setFailedLogoIds(new Set());
      setPagination((prev) => ({
        ...prev,
        total_count: data.total_count ?? 0,
      }));
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al cargar los clientes";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch whenever the page changes
  useEffect(() => {
    fetchClientes();
  }, [pagination.page]);

  // Returns the total number of pages based on total_count and page size
  const getTotalPages = () =>
    Math.max(1, Math.ceil(pagination.total_count / pagination.limit));

  // Validates bounds and updates the active page
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > getTotalPages()) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  // Controls edit modal visibility
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // The client currently selected for editing; null when no modal is open
  const [selectedCliente, setSelectedCliente] = useState(null);

  // Opens the edit modal with the given client pre-loaded
  const handleEditClick = (cliente) => {
    setSelectedCliente(cliente);
    setIsEditModalOpen(true);
  };

  // Closes the edit modal and clears the selected client
  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedCliente(null);
  };

  // Called by the modal after a successful save; refreshes the list
  const handleSaveCliente = () => {
    fetchClientes();
    handleCloseModal();
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const getClientInitials = (name = "") =>
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0] ?? "")
      .join("")
      .toUpperCase() || "CL";

  const handleLogoLoadError = (clienteId) => {
    setFailedLogoIds((prev) => {
      const next = new Set(prev);
      next.add(clienteId);
      return next;
    });
  };

  const [togglingIds, setTogglingIds] = useState(() => new Set());

  const handleToggleInvestigacion = async (cliente) => {
    if (togglingIds.has(cliente.id)) return;
    const newValue = !cliente.investigacion_habilitada;
    setTogglingIds((prev) => new Set(prev).add(cliente.id));
    try {
      const updated = await updateCliente(cliente.id, {
        investigacion_habilitada: newValue,
      });
      setClientes((prev) =>
        prev.map((c) =>
          c.id === cliente.id
            ? {
                ...c,
                investigacion_habilitada:
                  updated?.investigacion_habilitada ?? newValue,
              }
            : c,
        ),
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Error al actualizar el acceso a investigación",
      );
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(cliente.id);
        return next;
      });
    }
  };

  return (
    <>
      <div className="admin-clients-container">
        <Helmet>
          <title>BIOLAB</title>
          <meta property="og:title" content="Clientes - BIOLAB Platform" />
          <link
            rel="canonical"
            href="https://dev.portal.biolab.com.es/admin-clients"
          />
        </Helmet>
        <div className="window admin-clients-thq-window-elm">
          <header
            data-thq="thq-navbar"
            className="admin-clients-thq-navbar-elm"
          >
            <div
              data-thq="thq-navbar-nav"
              className="admin-clients-thq-desktop-menu-elm"
            >
              <div className="admin-clients-thq-top-elm1">
                <div className="admin-clients-thq-menus-elm1">
                  <Link
                    to="/admin-analysis"
                    className="admin-clients-thq-analysis-elm1 bodystandard"
                  >
                    <img
                      alt="Análisis"
                      src="/menu icons/analysis.svg"
                      className="admin-clients-thq-icon-elm10"
                    />
                    <span className="admin-clients-thq-label-elm10 caption">
                      Análisis
                    </span>
                  </Link>
                  <Link
                    to="/admin-report"
                    className="admin-clients-thq-reports-elm1 bodystandard"
                  >
                    <img
                      alt="Informes"
                      src="/menu icons/reports.svg"
                      className="admin-clients-thq-icon-elm11"
                    />
                    <span className="admin-clients-thq-label-elm11 caption">
                      Informes
                    </span>
                  </Link>
                  <button
                    type="button"
                    className="admin-clients-thq-admin-elm1 bodystandard"
                  >
                    <img
                      alt="Gestión"
                      src="/menu icons/admin.svg"
                      className="admin-clients-thq-icon-elm12"
                    />
                    <span className="admin-clients-thq-label-elm12 caption">
                      Gestión
                    </span>
                  </button>
                </div>
                <div className="admin-clients-thq-submenu-elm1">
                  <button
                    type="button"
                    className="admin-clients-thq-clients-elm1 bodystandard"
                  >
                    <img
                      alt="Clientes"
                      src="/menu icons/clients.svg"
                      className="admin-clients-thq-icon-elm13"
                    />
                    <span className="admin-clients-thq-label-elm13 caption">
                      Clientes
                    </span>
                  </button>
                  <Link
                    to="/admin-access"
                    className="admin-clients-thq-access-elm1 bodystandard"
                  >
                    <img
                      alt="Accesos"
                      src="/menu icons/access.svg"
                      className="admin-clients-thq-icon-elm14"
                    />
                    <span className="admin-clients-thq-label-elm14 caption">
                      Accesos
                    </span>
                  </Link>
                </div>
              </div>
              <button
                type="button"
                className="admin-clients-thq-exit-elm1 bodystandard"
                onClick={logout}
              >
                <img
                  alt="Salir"
                  src="/menu icons/exit.svg"
                  className="admin-clients-thq-icon-elm15"
                />
                <span className="admin-clients-thq-label-elm15 caption">
                  Salir
                </span>
              </button>
            </div>
            <div
              data-thq="thq-burger-menu"
              className="admin-clients-thq-burger-menu-elm"
              onClick={() => setIsMobileMenuOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  setIsMobileMenuOpen(true);
              }}
            >
              <svg viewBox="0 0 1024 1024" className="admin-clients-icon1">
                <path d="M128 554.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 298.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 810.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667z"></path>
              </svg>
            </div>
            <div
              data-thq="thq-mobile-menu"
              className={`admin-clients-thq-mobile-menu-elm${isMobileMenuOpen ? " admin-clients-thq-mobile-menu-open" : ""}`}
            >
              <div className="admin-clients-thq-nav-elm">
                <div className="admin-clients-thq-content-elm1">
                  <div className="admin-clients-thq-header-elm1">
                    <img
                      alt="BIOLAB Logo"
                      src="/biolab-logo-200h.png"
                      className="admin-clients-thq-biolablogo-elm1"
                    />
                    <div
                      data-thq="thq-close-menu"
                      className="admin-clients-thq-close-menu-elm"
                      onClick={closeMobileMenu}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          closeMobileMenu();
                      }}
                    >
                      <img
                        alt="image"
                        src="/close.svg"
                        className="admin-clients-thq-icon-elm16"
                      />
                    </div>
                  </div>
                  <div className="admin-clients-thq-top-elm2">
                    <div className="admin-clients-thq-menus-elm2">
                      <Link
                        to="/admin-analysis"
                        className="admin-clients-thq-analysis-elm2 bodystandard"
                        onClick={closeMobileMenu}
                      >
                        <img
                          alt="Análisis"
                          src="/menu icons/analysis.svg"
                          className="admin-clients-thq-icon-elm17"
                        />
                        <span className="admin-clients-thq-label-elm16">
                          Análisis
                        </span>
                      </Link>
                      <Link
                        to="/admin-report"
                        className="admin-clients-thq-reports-elm2 bodystandard"
                        onClick={closeMobileMenu}
                      >
                        <img
                          alt="Informes"
                          src="/menu icons/reports.svg"
                          className="admin-clients-thq-icon-elm18"
                        />
                        <span className="admin-clients-thq-label-elm17">
                          Informes
                        </span>
                      </Link>
                      <button
                        type="button"
                        className="admin-clients-thq-admin-elm2 bodystandard"
                      >
                        <img
                          alt="Gestión"
                          src="/menu icons/admin.svg"
                          className="admin-clients-thq-icon-elm19"
                        />
                        <span className="admin-clients-thq-label-elm18">
                          Gestión
                        </span>
                      </button>
                    </div>
                    <div className="admin-clients-thq-submenu-elm2">
                      <Link
                        to="/admin-clients"
                        className="admin-clients-thq-clients-elm2 bodystandard"
                        onClick={closeMobileMenu}
                      >
                        <img
                          alt="Clientes"
                          src="/menu icons/clients.svg"
                          className="admin-clients-thq-icon-elm20"
                        />
                        <span className="admin-clients-thq-label-elm19">
                          Clientes
                        </span>
                      </Link>
                      <Link
                        to="/admin-access"
                        className="admin-clients-thq-access-elm2 bodystandard"
                        onClick={closeMobileMenu}
                      >
                        <img
                          alt="Accesos"
                          src="/menu icons/access.svg"
                          className="admin-clients-thq-icon-elm21"
                        />
                        <span className="admin-clients-thq-label-elm20">
                          Accesos
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="admin-clients-thq-exit-elm2 bodystandard"
                  onClick={logout}
                >
                  <img
                    alt="Salir"
                    src="/menu icons/exit.svg"
                    className="admin-clients-thq-icon-elm22"
                  />
                  <span className="admin-clients-thq-label-elm21">Salir</span>
                </button>
              </div>
            </div>
          </header>
          <div className="admin-clients-thq-content-elm2">
            <div className="admin-clients-thq-title-elm1">
              <h1 className="admin-clients-thq-title-elm2">Clientes</h1>
              <img
                alt="BIOLAB Logo"
                src="/biolab-logo-200h.png"
                className="admin-clients-thq-biolablogo-elm2"
              />
            </div>
            <div className="admin-clients-thq-content-elm3">
              <div className="admin-clients-thq-table-elm">
                <div className="admin-clients-thq-header-elm2">
                  <span className="admin-clients-thq-text-elm10">
                    Nombre de la empresa
                  </span>
                  <span className="admin-clients-thq-text-elm11">Logo</span>
                  <span className="admin-clients-thq-text-elm12">
                    Identificador Marvin
                  </span>
                  <span className="admin-clients-thq-text-elm12">
                    Investigación
                  </span>
                  <span className="admin-clients-thq-text-elm13 bodysmall">
                    Editar
                  </span>
                </div>
                {/* Loading / error / empty states */}
                {loading && (
                  <div className="admin-clients-thq-row1-elm1">
                    <span className="bodysmall">Cargando...</span>
                  </div>
                )}
                {!loading && error && (
                  <div className="admin-clients-thq-row1-elm1">
                    <span className="bodysmall">{error}</span>
                  </div>
                )}
                {!loading && !error && clientes.length === 0 && (
                  <div className="admin-clients-thq-row1-elm1">
                    <span className="bodysmall">
                      No hay clientes registrados
                    </span>
                  </div>
                )}
                {/* Dynamic rows — one per client returned by the API */}
                {!loading &&
                  !error &&
                  clientes.map((cliente) => (
                    <div
                      key={cliente.id}
                      className="admin-clients-thq-row1-elm1"
                    >
                      <span className="admin-clients-thq-text-elm14 bodysmall">
                        {cliente.nombre_empresa}
                      </span>
                      {/* Logo: show image if url exists, otherwise show first two initials */}
                      <div className="admin-clients-thq-clientlogo-elm10">
                        {cliente.logo_url && !failedLogoIds.has(cliente.id) ? (
                          <img
                            alt={cliente.nombre_empresa}
                            src={cliente.logo_url}
                            className="admin-clients-thq-image4-elm1"
                            onError={() => handleLogoLoadError(cliente.id)}
                          />
                        ) : (
                          <span className="bodysmall">
                            {getClientInitials(cliente.nombre_empresa)}
                          </span>
                        )}
                      </div>
                      <span className="admin-clients-thq-text-elm15 bodysmall">
                        {cliente.identificador_marvin ?? "—"}
                      </span>
                      <button
                        type="button"
                        className={`admin-clients-investigacion-badge bodysmall${cliente.investigacion_habilitada ? " admin-clients-investigacion-badge--on" : ""}${togglingIds.has(cliente.id) ? " admin-clients-investigacion-badge--loading" : ""}`}
                        onClick={() => handleToggleInvestigacion(cliente)}
                        disabled={togglingIds.has(cliente.id)}
                      >
                        {togglingIds.has(cliente.id)
                          ? "..."
                          : cliente.investigacion_habilitada
                            ? "Habilitada"
                            : "Deshabilitada"}
                      </button>
                      {/* Edit button — opens the edit modal for this client */}
                      <div
                        className="admin-clients-thq-edit-elm1"
                        onClick={() => handleEditClick(cliente)}
                        style={{ cursor: "pointer" }}
                      >
                        <img
                          alt="Editar"
                          src="/edit.svg"
                          className="admin-clients-thq-icon-elm23"
                        />
                      </div>
                    </div>
                  ))}
              </div>
              <div className="admin-clients-thq-table-mobile-elm">
                {loading && (
                  <div className="admin-clients-mobile-card">
                    <span className="bodysmall">Cargando...</span>
                  </div>
                )}
                {!loading && error && (
                  <div className="admin-clients-mobile-card">
                    <span className="bodysmall">{error}</span>
                  </div>
                )}
                {!loading && !error && clientes.length === 0 && (
                  <div className="admin-clients-mobile-card">
                    <span className="bodysmall">
                      No hay clientes registrados
                    </span>
                  </div>
                )}
                {!loading &&
                  !error &&
                  clientes.map((cliente) => (
                    <div key={cliente.id} className="admin-clients-mobile-card">
                      <div className="admin-clients-mobile-card-header">
                        <span className="admin-clients-mobile-card-title">
                          {cliente.nombre_empresa}
                        </span>
                        <button
                          type="button"
                          className="admin-clients-mobile-card-edit"
                          onClick={() => handleEditClick(cliente)}
                        >
                          <img
                            alt="Editar"
                            src="/edit.svg"
                            className="admin-clients-thq-icon-elm29"
                          />
                        </button>
                      </div>
                      <div className="admin-clients-mobile-card-row">
                        <span className="bodysmall admin-clients-mobile-card-label">
                          Logo
                        </span>
                        <div className="admin-clients-thq-clientlogo-elm10 admin-clients-mobile-card-logo">
                          {cliente.logo_url &&
                          !failedLogoIds.has(cliente.id) ? (
                            <img
                              alt={cliente.nombre_empresa}
                              src={cliente.logo_url}
                              className="admin-clients-thq-image4-elm1"
                              onError={() => handleLogoLoadError(cliente.id)}
                            />
                          ) : (
                            <span className="bodysmall">
                              {getClientInitials(cliente.nombre_empresa)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="admin-clients-mobile-card-row">
                        <span className="bodysmall admin-clients-mobile-card-label">
                          Identificador
                        </span>
                        <span className="bodysmall">
                          {cliente.identificador_marvin ?? "—"}
                        </span>
                      </div>
                      <div className="admin-clients-mobile-card-row">
                        <span className="bodysmall admin-clients-mobile-card-label">
                          Diagnósticos
                        </span>
                        <span className="bodysmall">
                          {cliente.diagnosticos_count ?? 0}
                        </span>
                      </div>
                      <div className="admin-clients-mobile-card-row">
                        <span className="bodysmall admin-clients-mobile-card-label">
                          Usuarios
                        </span>
                        <span className="bodysmall">
                          {cliente.usuarios_count ?? 0}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Anterior
                </button>
                <span>
                  Página {pagination.page} de {getTotalPages()}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= getTotalPages()}
                >
                  Siguiente
                </button>
                <span>Total: {pagination.total_count} clientes</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Edit modal — uses a fixed overlay so it sits above all other content */}
      <EditClient
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        cliente={selectedCliente}
        onSave={handleSaveCliente}
      />
    </>
  );
};

export default AdminClients;
